import { useState, useEffect } from "react";
import { Users, Calendar, ClipboardCheck, TrendingUp, Plus, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import apiClient from "@/lib/api";

export default function OverviewPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient
      .get("/stats")
      .then((r) => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const statCards = stats
    ? [
        { label: "Total Players", value: stats.total_players, icon: Users, iconBg: "bg-green-50", iconColor: "text-green-600" },
        { label: "Total Sessions", value: stats.total_sessions, icon: Calendar, iconBg: "bg-blue-50", iconColor: "text-blue-600" },
        { label: "Sessions This Week", value: stats.sessions_this_week, icon: TrendingUp, iconBg: "bg-purple-50", iconColor: "text-purple-600" },
        { label: "Avg Attendance", value: `${stats.avg_attendance_rate}%`, icon: ClipboardCheck, iconBg: "bg-orange-50", iconColor: "text-orange-500" },
      ]
    : [];

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="overview-page">
      <div className="mb-8">
        <h1
          className="text-3xl font-black text-gray-900"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          Overview
        </h1>
        <p className="text-gray-600 mt-1">Welcome to your NextTackle dashboard</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((card) => (
          <div
            key={card.label}
            className="bg-white rounded-sm border border-gray-200 p-6"
            data-testid={`stat-${card.label.toLowerCase().replace(/ /g, "-")}`}
          >
            <div
              className={`w-10 h-10 ${card.iconBg} rounded-sm flex items-center justify-center mb-4`}
            >
              <card.icon className={`w-5 h-5 ${card.iconColor}`} />
            </div>
            <div
              className="text-2xl font-black text-gray-900 mb-1"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {card.value}
            </div>
            <div className="text-sm text-gray-500">{card.label}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2
              className="text-lg font-bold text-gray-900"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Recent Sessions
            </h2>
            <Link to="/dashboard/sessions">
              <Button variant="ghost" size="sm" className="text-green-600 hover:text-green-700 text-xs">
                View all <ArrowRight className="ml-1 w-3 h-3" />
              </Button>
            </Link>
          </div>

          {!stats?.recent_sessions?.length ? (
            <div className="text-center py-10 text-sm text-gray-500">
              No sessions yet.{" "}
              <Link to="/dashboard/sessions" className="text-green-600 hover:underline font-medium">
                Log your first session
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {stats.recent_sessions.map((s) => (
                <div key={s.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">{s.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {s.date} · {s.drill_type} · {s.intensity} intensity
                    </div>
                  </div>
                  <div className="text-sm text-gray-600 font-medium">
                    {s.attendance_count}/{s.total_players} present
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-sm border border-gray-200 p-6">
          <h2
            className="text-lg font-bold text-gray-900 mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Quick Actions
          </h2>
          <div className="space-y-3">
            <Link to="/dashboard/players" className="block">
              <Button
                className="w-full justify-start bg-green-600 hover:bg-green-700 text-white rounded-sm"
                data-testid="quick-add-player"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Player
              </Button>
            </Link>
            <Link to="/dashboard/sessions" className="block">
              <Button
                variant="outline"
                className="w-full justify-start rounded-sm border-gray-200"
                data-testid="quick-log-session"
              >
                <Calendar className="w-4 h-4 mr-2" /> Log Session
              </Button>
            </Link>
            <Link to="/dashboard/attendance" className="block">
              <Button
                variant="outline"
                className="w-full justify-start rounded-sm border-gray-200"
                data-testid="quick-take-attendance"
              >
                <ClipboardCheck className="w-4 h-4 mr-2" /> Take Attendance
              </Button>
            </Link>
          </div>

          {stats?.total_players === 0 && (
            <div className="mt-6 p-4 bg-green-50 rounded-sm border border-green-200">
              <p className="text-sm font-medium text-green-800 mb-1">Get started</p>
              <p className="text-xs text-green-700">
                Add your squad first, then log training sessions to see analytics.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
